import { useEffect, useMemo, useRef, useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { HEADER_LINKS, ROUTE_META } from '@/config/nav'
import { useAssetLibrary } from '@/context/AssetLibraryContext'
import { useAuth } from '@/context/AuthContext'
import { useJobCenter } from '@/context/JobCenterContext'
import { useSettings } from '@/context/SettingsContext'
import { Icon } from './icons'
import { Button } from './ui'

export function TopBar({
  onOpenAssets,
  onOpenSettings,
}: {
  onOpenAssets: () => void
  onOpenSettings: () => void
}) {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { settings, toggleSidebar } = useSettings()
  const { activeJobs } = useJobCenter()
  const { assets } = useAssetLibrary()
  const { user, logout, refresh } = useAuth()

  const meta = useMemo(() => {
    const exact = ROUTE_META[pathname]
    if (exact) return exact
    const hit = Object.entries(ROUTE_META).find(([p]) => p !== '/' && pathname.startsWith(p))
    return hit ? hit[1] : { title: '华数云工作台' }
  }, [pathname])

  /*
   * 顶栏直链 = `HEADER_ENTRIES` 里 `surface === 'link'` 的那几条，**全量**渲染。
   *
   * 这里曾经有一层 `filter((e) => !e.adminOnly || isAdmin)` —— 2026-10-07 拆表时
   * 连同配置里的 `adminOnly` 字段一起删掉了，原因见 config/nav.ts 的 HeaderEntry 段：
   * **会员永远不可能是后台账号**，那个过滤条件对本页的每一个用户都恒为 false，
   * 于是「后台管理」那条对谁都不显示（也不会有人想起来去查为什么）。
   *
   * 🔴 2026-10-09：**又删掉了一层** —— 原先还有
   * `filter((e) => e.to.split('?')[0] !== pathname)`（站在本页就藏掉指向本页的那条）。
   * 用户口径：「header 固定放商品资产」。落点是「固定」二字：
   * 顶栏的**组成必须不随当前页变化**，站哪都是同样那几颗。
   *
   * 为什么当初那层看着有道理、现在必须撤掉：
   *   · 它的收益是「省一颗按钮」，代价是**顶栏变成一块会自己变形的区域** ——
   *     用户刚在商品库页上认识的那一排在别处换了样子，反而要去两个地方找同一个入口；
   *   · 实测最刺眼的一处正是在商品库：**在商品库页上看不见「商品库」**，
   *     而「我从哪进来的 / 我是不是还在这一页」本来就靠这颗按钮的高亮来回答；
   *   · 加上那层之后，顶栏条数在 4~6 之间浮动，`smoke-nav` 与真机探针都得按页面
   *     分别算期望值 —— 一条「省位置」的优化换来两处需要分页面对账的断言。
   *
   * 藏掉它并不会让用户回不去首页之类的顾虑也一并消失：品牌区（左上角）本来就落在 `/`
   * （`HEADER_ENTRIES` 的 `surface: 'brand'`）。但它与「本页那颗按钮」是两件事，
   * 不该拿它当「藏起来也没关系」的理由。
   *
   * ⚠️ 高亮仍然是**按当前页**算的（下面的 `data-active`），这与「渲染哪些」是两件事 ——
   * 别因为「组成固定了」把高亮也一起删掉，那颗按钮亮不亮是用户判断自己在哪的唯一线索。
   * 判据用 `to.split('?')[0]` 而不是 `to`：`to` 里带 query 的条目（现在是 0 条，
   * 2026-09-29 的 AI 模特曾带 `?feature=`）用全等比**永远不相等** ⇒ 那颗按钮永不亮。
   */
  const links = HEADER_LINKS

  return (
    <header className="topbar">
      <Button variant="ghost" size="icon" icon="panel" onClick={toggleSidebar} aria-label="折叠侧栏" />
      {/*
        标题区**只有标题**。

        ⚠️ 这里原先还有一句 `meta.desc`（竖线隔开的灰字，比如「AI 模特库 |
        全部已生成的真人模特 · 搜索与按模特特征筛选 · 一次…」）。
        2026-10-09 整块删掉，用户原话「这些废话可以去掉，我要打造简洁的应用」。

        删它的依据不是"这句写得不好"，而是**它对多数页面本来就是第二处**：
          · `/ai-models` 的 desc 与这一页自己的 `CardHead hint` 说的是同一件事；
          · `/product-assets`、`/jobs`、`/my-works` 同理，页面卡片头那句更完整。
        顶栏那句话占的是**每次进这一页都要读一遍**的位置，说的却是页面里已经写着的东西。

        ⚠️ 别加回来。要让用户知道「这一页是干什么的」，写在页面自己的 `CardHead hint`
        —— 那是他正在操作的地方；顶栏只回答「我在哪」，并且这个答案已经由
        那一排直链的高亮给出了（见下面 `data-active` 那段）。
      */}
      <div className="topbar__head">
        <div className="topbar__title">{meta.title}</div>
      </div>

      <div className="topbar__spacer" />

      <div className="topbar__actions">
        {/*
          ⚠️ 这里原先有一颗常驻徽标：「已连后端 / MOCK 数据」（`API_MODE`）。
          2026-10-08 删掉，理由不是「不好看」而是**它是第二处**：
          `SettingsDialog` 的「数据层」块已经在显示同一件事，而且更完整
          （`VITE_API_MODE=http` 的值 + 一段「怎么切回 mock」的说明）。
          同一份状态摆两处，用户在两处看到的措辞还不一样（「已连后端」vs
          「http」），想问「现在到底连没连上」反而更绕。
          要看数据层状态 → 顶栏右侧那颗齿轮。
        */}

        {/*
          模板中心 / AI 模特 / 商品库 / 批量出图 —— 常驻直链。
          前几项原先在侧栏底部的「总览」组里，要滚动才看得到，且跟素材品类混在一起；
          现在固定在这里，任何页面都随手可点。
          ⚠️ 这里是**全量** map，没有条件过滤（见上面 `links` 那段的说明）：
          条数只由 nav.HEADER_LINKS 决定，不在这里手写、也不按当前页增减。
        */}
        {links.map((e) => (
          <NavLink
            key={e.id}
            to={e.to}
            className="topbar__link"
            data-active={pathname === e.to.split('?')[0]}
            title={e.hint}
          >
            <Icon name={e.icon} size={14} />
            <span className="topbar__link-text">{e.label}</span>
          </NavLink>
        ))}

        {/*
          任务中心 —— **点一次就落到页面**（2026-10-05 用户原话「任务中心点击直接跳转页面」）。

          在此之前它是一颗开浮层的按钮（`.popover`），浮层里是 8 条最近任务 + 一句
          「查看全部任务 →」。用户要看的正是**全部**，那个浮层只是多了一次点击：
          点开 → 扫一眼发现没有要找的 → 再点底部的链接 → 页面。两步路里第一步零信息量。

          所以浮层整块下线（连同里面的 TaskCard 列表、清空记录按钮、点击外部关闭的
          useEffect）。「最近任务」这个位置随浮层一起消失 —— 它从来不是用户要的粒度，
          任务列表只有一个真正的家，就是 `/jobs`（`smoke-nav` 里那条「四个位置只许
          出现 TaskCard」的断言同步收敛成三处，见脚本注释）。

          徽标上的数字保留：**正在跑的数量**是「要不要点进去」的唯一判据，
          有这个数字才能在任意页面一眼看出队列是不是空的。
        */}
        <Button
          variant={activeJobs.length ? 'primary' : 'outline'}
          size="sm"
          icon="activity"
          onClick={() => navigate('/jobs')}
          title="任务中心 · 全部生成记录与实时进度"
        >
          任务中心{activeJobs.length ? ` · ${activeJobs.length}` : ''}
        </Button>

        <Button variant="outline" size="sm" icon="archive" onClick={onOpenAssets}>
          素材库{assets.length ? ` · ${assets.length}` : ''}
        </Button>
        <Button variant="ghost" size="icon" icon="settings" onClick={onOpenSettings} aria-label="设置" />
        <AccountChip
          name={user?.displayName || user?.account || '未登录'}
          account={user?.account ?? ''}
          coins={user?.coins ?? 0}
          active={pathname === '/account'}
          onOpen={() => void refresh()}
          onAccount={() => navigate('/account')}
          onLogout={() => void logout()}
        />
      </div>
    </header>
  )
}

/**
 * 账号胶囊：余额 + 下拉菜单（账号中心 / 退出）。
 *
 * ### 为什么这一块不写进 `HEADER_LINKS`
 *
 * 顶栏配置里的每一条都是「去某个页面」的链接；这里是一个**状态 + 菜单**
 * （余额会变、菜单里有动作），两者的验证口径不同（前者钉路由，后者钉行为）。
 * 但它确实承载了 `HEADER_ENTRIES` 里 `surface: 'account'` 那一条（账号中心），
 * 所以闸门里有一条断言专门钉「这个承载关系还在」—— 见 scripts/smoke-nav.ts。
 *
 * ⚠️ 打开菜单时**顺手刷一次余额**：余额只在进站、提交、任务结束时更新，
 * 中间可能停了很久；用户点开看数字，就是想看「现在还剩多少」。
 *
 * ⚠️ 「额度账本」于 2026-10-07 从这里**搬走**（用户原话「点开可以新页面」）：
 * 它原先是个 52vh 的小弹层，账本在里面还得内滚动，而对账要能一眼看全。
 * 现在整块升级为 `/account`（账号信息 + 可用/已用额度 + 完整账本 + 退出登录）。
 * **不要在这里再加一条「额度账本」** —— 那就是同一件事两个入口。
 *
 * ⚠️ 「后台管理」也于 2026-10-07 从这里**删掉**了（同一天，但理由不同）。
 * 它不是搬走，是**这一侧根本没有**：拆表后后台账号住在 `admins` 表、
 * 走另一套登录，会员账号在那一侧「不存在」。菜单里留一条「后台管理」，
 * 点进去只会得到一张要求输入**另一套账号**的登录页 ——
 * 用户会以为是自己的会员账号出了问题。要进后台请直接敲 `/admin`。
 */
function AccountChip({
  name,
  account,
  coins,
  active,
  onOpen,
  onAccount,
  onLogout,
}: {
  name: string
  account: string
  coins: number
  active: boolean
  onOpen: () => void
  onAccount: () => void
  onLogout: () => void
}) {
  const [open, setOpen] = useState(false)
  const boxRef = useRef<HTMLDivElement | null>(null)

  // 点外面 / 按 Esc 关掉。
  // ⚠️ 只在**打开时**挂监听：一直挂着的话，每个鼠标点击都要过一遍 document 处理器。
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div className="accountchip" ref={boxRef}>
      <button
        type="button"
        className="accountchip__btn"
        data-open={open}
        data-active={active}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => {
          const next = !open
          setOpen(next)
          if (next) onOpen()
        }}
      >
        <span className="accountchip__avatar" aria-hidden>
          {name.slice(0, 1).toUpperCase()}
        </span>
        <span className="accountchip__meta">
          <span className="accountchip__name">{name}</span>
          {/* 余额单位是**积分**不是元（1 元 = 10 积分）—— 写「¥」会让用户
              按钱去对账，然后发现数字差了 10 倍 */}
          <span className="accountchip__coins">
            <Icon name="coin" size={11} />
            {coins.toFixed(1)} 积分
          </span>
        </span>
        <Icon name="chevron" size={13} />
      </button>

      {open ? (
        <div className="accountmenu" role="menu">
          <div className="accountmenu__head">
            <div className="accountchip__name">{name}</div>
            <div className="text-xs muted">{account}</div>
            <div className="accountmenu__bal">
              <span>可用额度</span>
              <strong>{coins.toFixed(1)}</strong>
            </div>
          </div>
          <button
            type="button"
            className="accountmenu__item"
            onClick={() => {
              setOpen(false)
              onAccount()
            }}
          >
            <Icon name="users" size={14} /> 账号中心
          </button>
          {/* 「后台管理」不在这里 —— 拆表后会员与后台是两套账号，
              这一条对每一个看得见本菜单的人都是死链。见组件上方那段说明。 */}
          <button type="button" className="accountmenu__item" onClick={() => { setOpen(false); onLogout() }}>
            <Icon name="x" size={14} /> 退出登录
          </button>
        </div>
      ) : null}
    </div>
  )
}
